import { useState } from "react";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { useDeviceType } from "@b2bportal/core-utilities";
import type { Travelers } from "@b2bportal/packages-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { ChildAgePicker } from "@tenant/hotels";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  Icon,
} from "@tenant/ui";
import { PackageChildAgePicker } from "./PackageChildAgePicker";
import { PackageCountPickerRow } from "./PackageCountPickerRow";
import defaultStyles from "./PackageTravellerPicker.module.scss";

const COMPONENT_KEY = "PackageTravellerPicker";
const MAXIMUM_TRAVELLERS = 6;

export type PackageTravellerPickerProps = {
  guests: Travelers;
  onGuestsChange: (guests: Travelers) => void;
  /** When true, shows Infants on lap and Infants in seat rows (for packages with flights). */
  showInfants: boolean;
};

export const PackageTravellerPicker = ({
  guests,
  onGuestsChange,
  showInfants,
}: PackageTravellerPickerProps) => {
  const { t } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();
  const [, cn] = useModuleBEM(defaultStyles, COMPONENT_KEY);

  // Draft state — initialised from props when the dialog opens so that
  // discarding the dialog (without Apply) leaves the parent state untouched.
  const [adults, setAdults] = useState(guests.adultCount);
  const [childAges, setChildAges] = useState<(number | null)[]>(
    guests.childAges
  );
  const [infantsOnLap, setInfantsOnLap] = useState(
    guests.infantOnLapAges.length
  );
  const [infantsInSeat, setInfantsInSeat] = useState(
    guests.infantInSeatAges.length
  );

  // Button label reflects committed guests prop so it stays accurate when
  // infants are cleared externally (e.g. switching to a non-flight package type).
  const committedCount =
    guests.adultCount +
    guests.childAges.length +
    guests.infantOnLapAges.length +
    guests.infantInSeatAges.length;

  // Draft total is used only inside the dialog for the maximum-reached check.
  const draftCount = adults + childAges.length + infantsOnLap + infantsInSeat;
  const maximumReached = draftCount >= MAXIMUM_TRAVELLERS;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      // Re-sync draft to current guests every time the dialog opens.
      setAdults(guests.adultCount);
      setChildAges(guests.childAges);
      setInfantsOnLap(guests.infantOnLapAges.length);
      setInfantsInSeat(guests.infantInSeatAges.length);
    }
  };

  const handleChildCountChange = (count: number) => {
    if (count > childAges.length) {
      setChildAges([
        ...childAges,
        ...Array<null>(count - childAges.length).fill(null),
      ]);
    } else {
      setChildAges(childAges.slice(0, count));
    }
  };

  const updateChildAge = (index: number, age: number) => {
    const updated = [...childAges] as (number | null)[];
    updated[index] = age;
    setChildAges(updated);
  };

  const hasUnselectedChildAges = childAges.some((age) => age === null);

  const handleApply = () => {
    onGuestsChange({
      adultCount: adults,
      childAges: childAges.filter((a): a is number => a !== null),
      infantOnLapAges: Array<number>(infantsOnLap).fill(0),
      infantInSeatAges: Array<number>(infantsInSeat).fill(1),
    });
  };

  const buttonLabel = t("core-hotels.search.guestPicker.buttonLabel", {
    count: committedCount,
  });

  const adultTitle = showInfants
    ? t("passengerCount.adultTitle")
    : t("core-hotels.search.guestPicker.adult.title");
  const adultSubtitle = showInfants
    ? t("passengerCount.adultSubtitle")
    : t("core-hotels.search.guestPicker.adult.subtitle");
  const childTitle = showInfants
    ? t("passengerCount.childrenTitle")
    : t("core-hotels.search.guestPicker.child.title");
  const childSubtitle = showInfants
    ? t("passengerCount.childrenSubtitle")
    : t("core-hotels.search.guestPicker.child.subtitle");

  const applyButton = (
    <div className={cn("apply-button-container")}>
      <DialogClose
        className={cn("apply-button")}
        onClick={handleApply}
        disabled={hasUnselectedChildAges}
      >
        {t("passengerCount.apply")}
      </DialogClose>
    </div>
  );

  return (
    <Dialog modal={false} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button type="button" className={cn("button")}>
          <Icon
            iconName={IconNameEnum.passengerSelection}
            className={cn("icon")}
          />
          <span className={cn("label-container")}>
            <span className={cn("label")}>{t("travellers")}</span>
            <span className={cn("count")}>{buttonLabel}</span>
          </span>
        </button>
      </DialogTrigger>

      <DialogContent
        title={t("passengerCount.modalTitle")}
        className={cn("dialog")}
        fullScreen={!isDesktopAndUp}
        showOverlay={true}
        bottomFixedContent={applyButton}
      >
        <div className={cn("dialog-content")}>
          <PackageCountPickerRow
            title={adultTitle}
            subtitle={adultSubtitle}
            minimumCount={1}
            count={adults}
            setCount={setAdults}
            maximumReached={maximumReached}
          />
          <PackageCountPickerRow
            title={childTitle}
            subtitle={childSubtitle}
            minimumCount={0}
            count={childAges.length}
            setCount={handleChildCountChange}
            maximumReached={maximumReached}
          />
          {childAges.map((age, index) =>
            showInfants ? (
              // Flight packages: restrict child ages to 2-11 (under 2 = infants)
              <PackageChildAgePicker
                key={index}
                childAge={age as number}
                onAgeSelectHandler={(newAge) => updateChildAge(index, newAge)}
                label={t(
                  "core-hotels.search.guestPicker.child.childIndexLabel",
                  { index: index + 1 }
                )}
                minAge={2}
                maxAge={11}
              />
            ) : (
              // Hotel+car packages: allow full 0-17 range
              <ChildAgePicker
                key={index}
                childAge={age as number}
                onAgeSelectHandler={(newAge) => updateChildAge(index, newAge)}
                label={t(
                  "core-hotels.search.guestPicker.child.childIndexLabel",
                  { index: index + 1 }
                )}
              />
            )
          )}
          {showInfants && (
            <PackageCountPickerRow
              title={t("passengerCount.infantLapTitle")}
              subtitle={t("passengerCount.infantLapSubtitle")}
              minimumCount={0}
              count={infantsOnLap}
              setCount={setInfantsOnLap}
              maximumReached={maximumReached}
            />
          )}
          {showInfants && (
            <PackageCountPickerRow
              title={t("passengerCount.infantSeatTitle")}
              subtitle={t("passengerCount.infantSeatSubtitle")}
              minimumCount={0}
              count={infantsInSeat}
              setCount={setInfantsInSeat}
              maximumReached={maximumReached}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
